/**
 * Step popover — hover summary card for flow nodes.
 *
 * Appears when the user hovers over a flow node, showing a quick summary
 * of the step's configuration and runtime status. Positioned absolutely
 * below (or above) the hovered node within the flow container.
 */

import { cn } from "@/lib/cn";
import type { FlowStep, FlowStepStatus } from "./types";
import { resolveIcon } from "./flow-nodes";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StepPopoverProps {
  step: FlowStep;
  /** Anchor position — the hovered node's position. */
  anchorX: number;
  anchorY: number;
  anchorWidth: number;
  anchorHeight: number;
  /** Container height — to decide if popover goes above or below. */
  containerHeight: number;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
  /** Mouse enter handler for hover-to-stay behavior. */
  onMouseEnter?: () => void;
  /** Mouse leave handler for hover-to-stay behavior. */
  onMouseLeave?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const POPOVER_WIDTH = 220;
const POPOVER_GAP = 6;

/** Format a duration in milliseconds to a human-readable string. */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/** Truncate a string at a max length, appending ellipsis if needed. */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + "\u2026";
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_BADGE: Record<FlowStepStatus, { label: string; className: string }> = {
  pending: {
    label: "Pending",
    className: "bg-muted/20 text-muted",
  },
  running: {
    label: "Running",
    className: "bg-badge-selected-fill text-status-selected",
  },
  completed: {
    label: "Done",
    className: "bg-success-muted text-success",
  },
  failed: {
    label: "Failed",
    className: "bg-danger-muted text-danger",
  },
  waiting: {
    label: "Waiting",
    className: "bg-warning-muted text-warning",
  },
};

function StatusBadge({ status }: { status: FlowStepStatus }) {
  const config = STATUS_BADGE[status];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-1.5 py-px text-canvas-sm font-medium leading-tight",
        config.className,
      )}
    >
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Body sections — one per step type
// ---------------------------------------------------------------------------

function ToolCallBody({ step }: { step: FlowStep }) {
  const params = step.toolParams;
  if (!params) return null;

  const entries = Object.entries(params).slice(0, 4);
  if (entries.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-baseline gap-1 text-canvas-base">
          <span className="flex-shrink-0 text-muted">{key}:</span>
          <span className="truncate text-secondary">{truncate(value, 30)}</span>
        </div>
      ))}
    </div>
  );
}

function AgentTaskBody({ step }: { step: FlowStep }) {
  if (!step.prompt) return null;
  return (
    <p className="line-clamp-2 text-canvas-base leading-relaxed text-secondary">
      {step.prompt}
    </p>
  );
}

function ApprovalGateBody({
  step,
  onApprove,
  onReject,
}: {
  step: FlowStep;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
}) {
  const isWaiting = step.status === "waiting";

  return (
    <div className="flex flex-col gap-1.5">
      {isWaiting && (
        <p className="text-canvas-base text-warning">
          Waiting for: <span className="font-medium text-secondary">user</span>
        </p>
      )}
      {isWaiting && (
        <div className="flex gap-1.5">
          <button
            className={cn(
              "rounded px-2 py-0.5 text-canvas-base font-medium",
              "bg-success-muted text-success hover:bg-success-muted/80",
              "transition-colors",
            )}
            data-testid={`step-popover-${step.id}-approve-btn`}
            onClick={(e) => {
              e.stopPropagation();
              onApprove?.(step.id);
            }}
          >
            Approve
          </button>
          <button
            className={cn(
              "rounded px-2 py-0.5 text-canvas-base font-medium",
              "bg-danger-muted text-danger hover:bg-danger-muted/80",
              "transition-colors",
            )}
            data-testid={`step-popover-${step.id}-reject-btn`}
            onClick={(e) => {
              e.stopPropagation();
              onReject?.(step.id);
            }}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function TriggerBody({ step }: { step: FlowStep }) {
  const config = step.triggerConfig;
  if (!config) return null;

  return (
    <div className="flex flex-col gap-0.5 text-canvas-base">
      <div className="flex items-baseline gap-1">
        <span className="text-muted">Type:</span>
        <span className="text-secondary capitalize">{config.type}</span>
      </div>
      {config.schedule && (
        <div className="flex items-baseline gap-1">
          <span className="text-muted">Schedule:</span>
          <span className="text-secondary">{config.schedule}</span>
        </div>
      )}
      {config.timezone && (
        <div className="flex items-baseline gap-1">
          <span className="text-muted">Timezone:</span>
          <span className="text-secondary">{config.timezone}</span>
        </div>
      )}
      {config.eventDescription && (
        <div className="flex items-baseline gap-1">
          <span className="text-muted">Event:</span>
          <span className="truncate text-secondary">{truncate(config.eventDescription, 30)}</span>
        </div>
      )}
    </div>
  );
}

function ConditionalBody({ step }: { step: FlowStep }) {
  const branches = step.branches;
  if (!branches || branches.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {branches.map((branch) => (
        <span
          key={branch.targetStepId}
          className="rounded-full bg-glass-fill-heavy px-1.5 py-px text-canvas-sm text-secondary"
        >
          {branch.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body dispatcher
// ---------------------------------------------------------------------------

function PopoverBody({
  step,
  onApprove,
  onReject,
}: {
  step: FlowStep;
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
}) {
  switch (step.type) {
    case "tool_call":
      return <ToolCallBody step={step} />;
    case "agent_task":
      return <AgentTaskBody step={step} />;
    case "approval_gate":
      return (
        <ApprovalGateBody step={step} onApprove={onApprove} onReject={onReject} />
      );
    case "trigger":
      return <TriggerBody step={step} />;
    case "conditional":
      return <ConditionalBody step={step} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Footer — status text + duration
// ---------------------------------------------------------------------------

function PopoverFooter({ step }: { step: FlowStep }) {
  const { status, durationMs, liveStatus, output } = step;

  let statusText: string;
  switch (status) {
    case "pending":
      statusText = "Pending";
      break;
    case "running":
      statusText = durationMs != null ? `Running \u00B7 ${formatDuration(durationMs)}` : "Running";
      break;
    case "completed":
      statusText = durationMs != null ? `Completed \u00B7 ${formatDuration(durationMs)}` : "Completed";
      break;
    case "failed":
      statusText = durationMs != null ? `Failed \u00B7 ${formatDuration(durationMs)}` : "Failed";
      break;
    case "waiting":
      statusText = "Waiting for approval";
      break;
    default:
      statusText = "Unknown";
  }

  // For running steps, show liveStatus below the main status line.
  // For failed steps, show first line of output as error hint.
  const detail =
    status === "running" && liveStatus
      ? liveStatus
      : status === "failed" && output
        ? output.split("\n")[0]
        : null;

  return (
    <div className="flex flex-col gap-0.5">
      <span
        className={cn(
          "text-canvas-base font-medium",
          status === "completed" && "text-success",
          status === "failed" && "text-danger",
          status === "running" && "text-status-selected",
          status === "waiting" && "text-warning",
          status === "pending" && "text-muted",
        )}
      >
        {statusText}
      </span>
      {detail && (
        <span className="truncate text-canvas-sm text-muted">{truncate(detail, 60)}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepPopover — main component
// ---------------------------------------------------------------------------

export function StepPopover({
  step,
  anchorX,
  anchorY,
  anchorWidth,
  anchorHeight,
  containerHeight,
  onApprove,
  onReject,
  onMouseEnter,
  onMouseLeave,
}: StepPopoverProps) {
  const Icon = resolveIcon(step.icon);

  // Position: center horizontally below the node, flip above if near bottom.
  const left = anchorX + anchorWidth / 2 - POPOVER_WIDTH / 2;
  const belowY = anchorY + anchorHeight + POPOVER_GAP;

  // Estimate popover height ~120px for the flip check.
  const estimatedHeight = 120;
  const fitsBelow = belowY + estimatedHeight < containerHeight;
  const top = fitsBelow ? belowY : anchorY - estimatedHeight - POPOVER_GAP;

  return (
    <div
      className={cn(
        "absolute z-50 flex flex-col gap-2",
        "rounded-lg border border-glass-stroke",
        "bg-glass-fill-dense backdrop-blur-glass shadow-lg",
        "p-2.5 pointer-events-auto",
      )}
      style={{
        left: Math.max(0, left),
        top: Math.max(0, top),
        width: POPOVER_WIDTH,
      }}
      data-testid={`step-popover-${step.id}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header: icon + title + status badge */}
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 flex-shrink-0 text-secondary" />
        <span className="flex-1 truncate text-canvas-md font-medium text-secondary">
          {step.title}
        </span>
        <StatusBadge status={step.status} />
      </div>

      {/* Body: varies by step type */}
      <PopoverBody step={step} onApprove={onApprove} onReject={onReject} />

      {/* Footer: status + duration */}
      <div className="border-t border-glass-stroke-light pt-1.5">
        <PopoverFooter step={step} />
      </div>
    </div>
  );
}

export { formatDuration };
